import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, OctagonX, Play, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const CONSENT_KEY = "vts_risk_consent_v1";
export const hasRiskConsent = () => localStorage.getItem(CONSENT_KEY) === "1";

const CONSENT_ITEMS = [
  "I understand that trading losses can and do occur",
  "VTS does not guarantee profit",
  "VTS does not guarantee limited losses — I can lose my stake",
  "I am not trading borrowed money",
  "I am not using money needed for living expenses",
  "I know I can pause or stop the bots at any time",
];

export const RiskConsentCard = ({ onAccept }) => {
  const [checks, setChecks] = useState(() => CONSENT_ITEMS.map(() => false));
  const all = checks.every(Boolean);
  return (
    <div data-testid="risk-consent-card" className="rounded-xl border border-amber-500/30 bg-vts-panel p-5 max-w-2xl mx-auto">
      <p className="text-sm font-black text-white inline-flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-400" /> Before you use automation — read and confirm
      </p>
      <p className="text-[11px] font-mono text-vts-silver/60 mt-1 mb-3">
        Automated trading is risky. Confirm each point to unlock the bots. This is required once per device.
      </p>
      <div className="space-y-2">
        {CONSENT_ITEMS.map((t, i) => (
          <label key={i} className="flex items-start gap-2.5 rounded-lg border border-vts-line bg-vts-surface/40 px-3 py-2 cursor-pointer">
            <input data-testid={`risk-consent-check-${i + 1}`} type="checkbox" checked={checks[i]}
              onChange={(e) => setChecks((c) => c.map((v, j) => (j === i ? e.target.checked : v)))}
              className="mt-0.5 accent-[#2962FF]" />
            <span className="text-[11px] font-mono text-white/80 leading-snug">{t}</span>
          </label>
        ))}
      </div>
      <button data-testid="risk-consent-accept" disabled={!all}
        onClick={() => { localStorage.setItem(CONSENT_KEY, "1"); onAccept(); }}
        className="mt-4 w-full h-11 rounded-lg bg-[#2962FF] hover:bg-[#1E4ECA] disabled:opacity-40 text-white text-sm font-bold transition-colors">
        {all ? "I understand — unlock automation" : "Tick every box to continue"}
      </button>
    </div>
  );
};

/** Always-visible safety bar on bot tabs: day P&L vs limits, recovery
 * opt-in (OFF by default), daily-loss + stake caps, emergency stop. */
const RiskSafetyBar = ({ accountId }) => {
  const [st, setSt] = useState(null);
  const [dailyPct, setDailyPct] = useState("");
  const [maxStake, setMaxStake] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/risk/status?account_id=${accountId || ""}`);
      setSt(r.data);
      setDailyPct((p) => p === "" ? String(r.data.settings.max_daily_loss_pct) : p);
      setMaxStake((p) => p === "" ? String(r.data.settings.max_stake_abs) : p);
    } catch { /* bar renders skeleton */ }
  }, [accountId]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const r = await api.post("/risk/settings", {
        account_id: accountId || "",
        settings: { max_daily_loss_pct: Number(dailyPct), max_stake_abs: Number(maxStake), ...extra },
      });
      setSt((s) => ({ ...s, settings: r.data }));
      toast.success("Risk limits saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const toggleRecovery = async (on) => {
    try {
      const r = await api.post("/risk/settings", { account_id: accountId || "", settings: { recovery_enabled: on } });
      setSt((s) => ({ ...s, settings: r.data }));
      toast(on
        ? "Recovery staking ENABLED — stakes may rise after losses. Higher risk, your call."
        : "Recovery staking OFF — flat stakes only (recommended)");
    } catch { toast.error("Save failed"); }
  };

  const kill = async () => {
    try { await api.post("/risk/kill"); await load(); toast.success("EMERGENCY STOP — every bot buy is now blocked"); }
    catch { toast.error("Failed"); }
  };
  const resume = async () => {
    try { await api.post("/risk/resume"); await load(); toast.success("Trading resumed"); }
    catch { toast.error("Failed"); }
  };

  const s = st?.settings;
  const killed = !!s?.kill_switch;
  const dayNet = st?.day_net ?? 0;
  const limit = st?.day_start_balance ? -(st.day_start_balance * (s?.max_daily_loss_pct || 2) / 100) : null;

  return (
    <div data-testid="risk-safety-bar" className={`rounded-xl border p-3 ${killed ? "border-vts-loss/50 bg-vts-loss/5" : "border-vts-line bg-vts-panel"}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-white/45 inline-flex items-center gap-1.5">
          <ShieldAlert className={`w-3.5 h-3.5 ${killed ? "text-vts-loss" : "text-[#2962FF]"}`} /> Risk & Safety
        </p>
        <p data-testid="risk-day-net" className="text-[11px] font-mono text-white/70">
          today <span className={`font-bold ${dayNet >= 0 ? "text-vts-profit" : "text-vts-loss"}`}>{dayNet >= 0 ? "+" : "−"}${Math.abs(dayNet).toFixed(2)}</span>
          {limit != null && <span className="text-white/40"> / stop at −${Math.abs(limit).toFixed(2)}</span>}
          {st?.paused_for_s > 0 && <span className="text-amber-400 font-bold"> · loss-streak pause {Math.ceil(st.paused_for_s / 60)}m</span>}
        </p>
        <label className="flex items-center gap-1.5 text-[10px] font-mono text-white/60">
          daily stop %
          <input data-testid="risk-daily-loss-input" type="number" min="0.5" max="10" step="0.5" value={dailyPct}
            onChange={(e) => setDailyPct(e.target.value)}
            className="w-16 px-2 h-8 rounded bg-vts-surface border border-vts-line text-white text-[11px] font-mono focus:border-[#2962FF] focus:outline-none" />
        </label>
        <label className="flex items-center gap-1.5 text-[10px] font-mono text-white/60">
          max stake $
          <input data-testid="risk-max-stake-input" type="number" min="0.35" step="1" value={maxStake}
            onChange={(e) => setMaxStake(e.target.value)}
            className="w-16 px-2 h-8 rounded bg-vts-surface border border-vts-line text-white text-[11px] font-mono focus:border-[#2962FF] focus:outline-none" />
        </label>
        <button data-testid="risk-save-btn" onClick={() => save()} disabled={saving}
          className="h-8 px-2.5 rounded bg-vts-surface border border-vts-line hover:border-white/30 text-[10px] font-bold text-white/80 inline-flex items-center gap-1 transition-colors disabled:opacity-40">
          <Save className="w-3 h-3" /> Save
        </button>
        <label className="flex items-center gap-1.5 text-[10px] font-mono text-white/60">
          recovery staking
          <Switch data-testid="risk-recovery-toggle" checked={!!s?.recovery_enabled} onCheckedChange={toggleRecovery} />
          <span className={s?.recovery_enabled ? "text-amber-400 font-bold" : "text-white/40"}>{s?.recovery_enabled ? "ON (risky)" : "OFF"}</span>
        </label>
        <div className="ml-auto">
          {killed ? (
            <button data-testid="risk-resume-btn" onClick={resume}
              className="h-9 px-3 rounded-lg bg-vts-profit hover:opacity-90 text-black text-[11px] font-black inline-flex items-center gap-1.5 transition-opacity">
              <Play className="w-3.5 h-3.5" /> RESUME TRADING
            </button>
          ) : (
            <button data-testid="risk-kill-btn" onClick={kill}
              className="h-9 px-3 rounded-lg bg-[#F23645] hover:bg-[#d92c3a] text-white text-[11px] font-black inline-flex items-center gap-1.5 transition-colors">
              <OctagonX className="w-3.5 h-3.5" /> EMERGENCY STOP
            </button>
          )}
        </div>
      </div>
      <p className="text-[9px] font-mono text-white/35 mt-1.5">
        Flat stakes by default — stakes never rise after a loss unless you switch recovery on. Automated trading can lose money; historical results are not guarantees.
      </p>
    </div>
  );
};

export default RiskSafetyBar;
